import { Crosshair, Sparkles, Workflow, MessagesSquare, CheckCircle2, BarChart3, Check, ArrowRight, ChevronRight } from "lucide-react";
import { ECOSYSTEM_STAGES, PLATFORM_BULLETS, IMAGES } from "@/lib/site";
import DashboardPreview from "./DashboardPreview";

const STAGE_ICONS = { Crosshair, Sparkles, Workflow, MessagesSquare, CheckCircle2, BarChart3 };

/** Clean straight sapphire connection line — sits BEHIND icons */
function ConnectionLine() {
  return (
    <div
      className="pointer-events-none absolute left-[7.5%] right-[7.5%] top-[42px] hidden md:block h-px z-0"
      style={{
        background:
          "linear-gradient(90deg, rgba(96,165,250,0) 0%, rgba(96,165,250,0.65) 14%, rgba(155,199,255,0.95) 50%, rgba(96,165,250,0.65) 86%, rgba(96,165,250,0) 100%)",
        boxShadow: "0 0 12px rgba(96,165,250,0.55), 0 0 24px rgba(37,99,235,0.28)",
        animation: "wavePulse 3.6s ease-in-out infinite",
      }}
      aria-hidden
    />
  );
}

/** Directional arrows shown statically between two stages with a subtle pulse */
function StageArrow({ delay = 0 }) {
  return (
    <div
      className="pointer-events-none absolute top-[34px] hidden md:flex items-center justify-center w-6 h-4 z-10"
      style={{ left: "calc(100% - 12px)" }}
      aria-hidden
    >
      <div className="relative w-6 h-3 overflow-hidden">
        <ChevronRight
          className="absolute inset-0 m-auto w-3.5 h-3.5 text-sapphire-glow arrow-travel"
          style={{ animationDelay: `${delay}s` }}
          strokeWidth={2.5}
        />
      </div>
    </div>
  );
}

export default function IntelligenceAndPlatform({ onBookDemo }) {
  return (
    <section id="platform" className="relative pt-4 pb-16 lg:pb-20" data-testid="ecosystem-section">
      <div className="absolute inset-0 bg-grid opacity-[0.18]" aria-hidden />
      <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10">
        {/* Top row: ECOSYSTEM (left ~60%) + PLATFORM COPY w/ background image (right ~40%) */}
        <div className="grid lg:grid-cols-[1.5fr_1fr] gap-6 lg:gap-8">
          {/* LEFT — Ecosystem flow */}
          <div className="relative">
            <div className="text-center max-w-2xl mx-auto">
              <div className="eyebrow" data-testid="ecosystem-eyebrow">
                <span className="inline-block w-6 h-px bg-sapphire-glow/60 mr-3 align-middle" />
                THE ESTHERA INTELLIGENCE ECOSYSTEM
                <span className="inline-block w-6 h-px bg-sapphire-glow/60 ml-3 align-middle" />
              </div>
              <p
                className="mt-4 text-[14.5px] lg:text-[16px] text-platinum/70 leading-[1.55]"
                data-testid="ecosystem-title"
              >
                From inquiry to intelligence. From data to decisions. From operations to outcomes.
              </p>
            </div>

            <div className="relative mt-10">
              <ConnectionLine />
              <div className="grid grid-cols-3 md:grid-cols-6 gap-y-10 gap-x-2 relative">
                {ECOSYSTEM_STAGES.map((s, i) => {
                  const Icon = STAGE_ICONS[s.icon];
                  return (
                    <div
                      key={s.key}
                      className="relative flex flex-col items-center text-center group z-10"
                      data-testid={`ecosystem-stage-${s.key}`}
                    >
                      {/* Directional arrow toward next stage */}
                      {i < ECOSYSTEM_STAGES.length - 1 && <StageArrow delay={i * 0.45} />}

                      <div className="relative breathe" style={{ animationDelay: `${i * 0.2}s` }}>
                        {/* Outer glow halo */}
                        <div className="absolute inset-0 rounded-full bg-sapphire/40 blur-2xl group-hover:bg-sapphire/65 transition-all duration-500" />
                        {/* Rotating outer ring */}
                        <div
                          className="absolute -inset-2 rounded-full ring-spin"
                          style={{
                            background:
                              "conic-gradient(from 0deg, rgba(96,165,250,0) 0deg, rgba(96,165,250,0.65) 80deg, rgba(96,165,250,0) 180deg, rgba(96,165,250,0.40) 280deg, rgba(96,165,250,0) 360deg)",
                            WebkitMask:
                              "radial-gradient(closest-side, transparent calc(100% - 1.5px), #000 calc(100% - 1px))",
                            mask:
                              "radial-gradient(closest-side, transparent calc(100% - 1.5px), #000 calc(100% - 1px))",
                          }}
                          aria-hidden
                        />
                        {/* Inner counter-rotating ring */}
                        <div
                          className="absolute -inset-[2px] rounded-full ring-spin-rev opacity-80"
                          style={{
                            background:
                              "conic-gradient(from 90deg, rgba(155,199,255,0) 0deg, rgba(155,199,255,0.65) 60deg, rgba(155,199,255,0) 180deg, rgba(155,199,255,0.45) 270deg, rgba(155,199,255,0) 360deg)",
                            WebkitMask:
                              "radial-gradient(closest-side, transparent calc(100% - 1px), #000 calc(100% - 0.5px))",
                            mask:
                              "radial-gradient(closest-side, transparent calc(100% - 1px), #000 calc(100% - 0.5px))",
                          }}
                          aria-hidden
                        />
                        <div
                          className="relative w-[76px] h-[76px] rounded-full inline-flex items-center justify-center group-hover:scale-[1.04] transition-transform duration-500"
                          style={{
                            background:
                              "radial-gradient(circle at 30% 30%, rgba(96,165,250,0.24), #0E1422 70%)",
                            border: "1px solid rgba(96,165,250,0.46)",
                            boxShadow:
                              "0 0 30px -6px rgba(96,165,250,0.60), inset 0 0 30px rgba(37,99,235,0.24)",
                          }}
                        >
                          <Icon className="w-7 h-7 text-sapphire-glow" />
                        </div>
                      </div>
                      <div className="font-display mt-6 text-platinum text-[18px] leading-tight">
                        {s.title}
                      </div>
                      <div className="mt-1.5 text-[11.5px] text-platinum/50 max-w-[150px] leading-snug">
                        {s.desc}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* RIGHT — Platform copy with full architectural visual (Image 2) */}
          <div className="relative rounded-2xl overflow-hidden border border-white/[0.07]" data-testid="platform-section">
            <img
              src={IMAGES.platformArch}
              alt="Luxury waterfront commercial property at night"
              className="absolute inset-0 w-full h-full object-cover object-center"
              draggable="false"
            />
            {/* Subtle dark gradient on top-left only — keep building & reflections visible */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "linear-gradient(120deg, rgba(10,15,26,0.92) 0%, rgba(10,15,26,0.78) 35%, rgba(10,15,26,0.20) 70%, rgba(10,15,26,0) 100%)",
              }}
            />
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "linear-gradient(180deg, rgba(10,15,26,0.10) 0%, rgba(10,15,26,0) 30%, rgba(10,15,26,0) 70%, rgba(10,15,26,0.45) 100%)",
              }}
            />

            <div className="relative px-7 lg:px-9 py-9 lg:py-10 max-w-[520px]">
              <div className="eyebrow" data-testid="platform-eyebrow">
                <span className="inline-block w-6 h-px bg-sapphire-glow/60 mr-3 align-middle" />
                ONE PLATFORM. ENDLESS IMPACT.
              </div>
              <h3
                className="font-display mt-4 text-platinum text-[28px] lg:text-[34px] leading-[1.1] tracking-[-0.015em]"
                data-testid="platform-headline"
              >
                Everything you need
                <br />
                to run <span className="text-sapphire-glow">smarter operations.</span>
              </h3>
              <p className="mt-4 text-[14px] text-platinum/75 leading-[1.65] max-w-[420px]">
                From lead to close, every workflow is powered by AI and built for real estate.
              </p>

              <ul className="mt-6 space-y-3" data-testid="platform-features">
                {PLATFORM_BULLETS.map((b, i) => (
                  <li
                    key={b}
                    className="flex items-center gap-3 text-[14px] text-platinum"
                    data-testid={`platform-bullet-${i}`}
                  >
                    <span className="w-5 h-5 rounded-md border border-sapphire-glow/40 bg-sapphire/15 inline-flex items-center justify-center shrink-0">
                      <Check className="w-3 h-3 text-sapphire-glow" strokeWidth={3} />
                    </span>
                    <span style={{ textShadow: "0 1px 8px rgba(10,15,26,0.85)" }}>{b}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-7 flex flex-wrap items-center gap-3">
                <button
                  onClick={onBookDemo}
                  data-testid="platform-book-demo"
                  className="btn-primary rounded-md h-12 px-6 text-[14px] font-medium inline-flex items-center gap-2"
                >
                  Book a Demo
                  <ArrowRight className="w-4 h-4" />
                </button>
                <a
                  href="/platform"
                  data-testid="platform-explore-link"
                  className="inline-flex items-center gap-2 text-[13.5px] text-sapphire-glow hover:text-white transition-colors"
                >
                  Explore the Platform <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom — Dashboard preview spans wider, anchored under ecosystem */}
        <div className="mt-10">
          <DashboardPreview />
          <div className="mt-3 flex items-center justify-between text-[10.5px] tracking-[0.22em] text-platinum/40 uppercase">
            <span>Enterprise Operating System · Live Console</span>
            <span>Esthera RealityOS</span>
          </div>
        </div>
      </div>
    </section>
  );
}
